// React and libraries
import React, { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Box,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
} from "@mui/material";
import { PersonOutline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";

// Images
import authEffect2 from "../../assets/images/effect-pattern/auth-effect-2.png";
import authEffect from "../../assets/images/effect-pattern/auth-effect.png";
import bgImage from "../../assets/images/new-login-card-bg.png";
import bgImage2 from "../../assets/images/new-login-card-bg2.png";
import bgImageDark from "../../assets/images/new-login-card-drk.png";
import bgImageDark2 from "../../assets/images/new-login-card-drk2.png";
import Axios from "../../Common/api/axiosConfig";

// Main Component
const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Determine background images based on theme mode
  const leftBgImage = theme.palette.mode === "dark" ? bgImageDark : bgImage;
  const rightBgImage = theme.palette.mode === "dark" ? bgImageDark2 : bgImage2;

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setError("");
  };

  // Formik validation schema
  const validation = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Please enter your email")
        .email("Invalid email"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError("");

      try {
        // API call to auth/login endpoint using Axios
        const response = await Axios.post("/auth/login", {
          email: values.email,
        });

        // API hit successful - navigate to OTP screen
        console.log("Login code sent to:", values.email);
        console.log("API Response:", response.data);

        navigate("/otp-screen", {
          state: {
            email: values.email,

            ...response.data,
          },
          replace: true,
        });
      } catch (error: any) {
        if (error.response) {
          const errorMessage =
            error.response.data?.message ||
            error.response.data?.error ||
            "Failed to send login code. Please try again.";
          setError(errorMessage);
          console.error("API Error:", error.response.data);
        } else if (error.request) {
          // Request was made but no response received
          setError(
            "Network error. Please check your connection and try again."
          );
          console.error("Network Error:", error.request);
        } else {
          // Something else happened
          setError("An unexpected error occurred. Please try again.");
          console.error("Error:", error.message);
        }

        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      {/* Page Title */}
      <title>Login | Rudrix Store</title>

      {/* Error Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Outer container */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 2,
          background:
            theme.palette.mode === "dark"
              ? "#1D2127"
              : theme.palette.background.default,
          overflow: "hidden",
        }}
      >
        {/* Decorative background circles */}
        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: theme.palette.primary.light,
            opacity: 0.1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -80,
            right: -80,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: theme.palette.secondary.light,
            opacity: 0.1,
          }}
        />

        {/* Card container */}
        <Grid container justifyContent="center" maxWidth="950px">
          <Grid item xs={12} lg={10} xl={9}>
            <Card
              elevation={8}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: theme.shadows[8],
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "#1D2127"
                    : theme.palette.background.paper,
              }}
            >
              <Grid container spacing={0}>
                {/* Left panel - Image */}
                <Grid
                  item
                  xs={12}
                  md={3}
                  sx={{
                    background: `url(${leftBgImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundColor: theme.palette.primary.main,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: -40,
                      top: -40,
                      opacity: 0.3,
                    }}
                  >
                    <img src={authEffect} alt="Effect" />
                  </Box>
                </Grid>

                {/* Middle panel - Login Form */}
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "#1D2127"
                        : theme.palette.background.paper,
                  }}
                >
                  <CardContent
                    sx={{
                      p: { xs: 4, md: 6 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    {/* Heading */}
                    <Box sx={{ textAlign: "center", mb: 4 }}>
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        color="primary"
                        gutterBottom
                        sx={{
                          whiteSpace: { xs: "normal", sm: "nowrap" },
                          fontSize: {
                            xs: "1rem",
                            sm: "1.2rem",
                            md: "1.5rem",
                          },
                          textAlign: { xs: "center", sm: "left" },
                          px: { xs: 2, sm: 0 },
                        }}
                      >
                        Welcome To Rudrix Store
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ opacity: 0.8 }}
                      >
                        Enter your email to receive login code
                      </Typography>
                    </Box>

                    {/* Form */}
                    <Box
                      component="form"
                      onSubmit={validation.handleSubmit}
                      noValidate
                      sx={{ mt: 2 }}
                    >
                      {/* Email field */}
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Enter your email"
                        name="email"
                        label="Email"
                        value={validation.values.email}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        error={
                          validation.touched.email &&
                          Boolean(validation.errors.email)
                        }
                        helperText={
                          validation.touched.email && validation.errors.email
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonOutline
                                color={
                                  validation.touched.email &&
                                  validation.errors.email
                                    ? "error"
                                    : "action"
                                }
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 3,
                          "& .MuiOutlinedInput-root": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#2A2F36"
                                : "transparent",
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      />

                      {/* Submit button */}
                      <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={loading}
                        size="large"
                        sx={{
                          mt: 3,
                          py: 1.5,
                          borderRadius: 2,
                          fontSize: "1rem",
                          fontWeight: 600,
                          backgroundColor: theme.palette.primary.main,
                          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                          boxShadow: theme.shadows[4],
                          "&:hover": {
                            boxShadow: theme.shadows[8],
                            backgroundColor: theme.palette.primary.dark,
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                          },
                          "&:disabled": {
                            backgroundColor: theme.palette.action.disabled,
                            background: theme.palette.action.disabledBackground,
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Send Login Code"
                        )}
                      </Button>

                      {/* Footer text */}
                      <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.875rem",
                            opacity: 0.7,
                          }}
                        >
                          ©{new Date().getFullYear()} Rudrix. All rights
                          reserved.
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Grid>

                {/* Right panel - Image */}
                <Grid
                  item
                  xs={12}
                  md={3}
                  sx={{
                    background: `url(${rightBgImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundColor: theme.palette.secondary.main,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      right: -20,
                      bottom: -20,
                      opacity: 0.3,
                      transform: "rotate(45deg)",
                    }}
                  >
                    <img src={authEffect2} alt="Effect 2" />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Login;
